import { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'

import { useIsMobile } from '@/utils/useMobile'
import {
  LocationContext,
  getLocationContext,
  GERMAN_CITY_NAMES
} from '@/utils/getLocationContext'

import HeroWrapper from '../common/HeroWrapper'
import Title from '../common/Title'
import { TitleWrapper } from '../home/Discover/Discover.styled'

interface HeroSearchResultsProps {
  text?: string | ReactNode
  parkingsCount?: number | string
  locationContext?: LocationContext
}

export default function HeroSearchResults({
  text,
  parkingsCount,
  locationContext
}: HeroSearchResultsProps) {
  const { isMobile } = useIsMobile()
  const { t, i18n } = useTranslation(['parkings', 'common'])

  const getDynamicHeaderText = () => {
    if (!locationContext || !parkingsCount) {
      const count =
        typeof parkingsCount === 'string'
          ? parseInt(parkingsCount)
          : parkingsCount || 0
      const isPlural = count !== 1
      const isPolishFew = i18n.language === 'pl' && count >= 2 && count <= 4

      const translationKey = !isPlural
        ? 'parkings:hero.parkingSearchResultsSingle'
        : isPolishFew
        ? 'parkings:hero.parkingSearchResultsFew'
        : 'parkings:hero.parkingSearchResults'

      return (
        text ||
        t(translationKey as 'parkings:hero.parkingSearchResults', {
          value: parkingsCount
        })
      )
    }

    const {
      isAirport,
      cityName,
      airportName,
      locativeForm,
      cityKey,
      cityUrlSlug
    } = locationContext
    const locale = i18n.language || 'en'

    if (isAirport && airportName) {
      let airportDisplayName = airportName
      try {
        const translatedName = t(`common:${airportName}` as any, {
          defaultValue: airportName
        })
        if (translatedName !== airportName) {
          airportDisplayName = translatedName
        }
      } catch {
        airportDisplayName = airportName
      }

      const count =
        typeof parkingsCount === 'string'
          ? parseInt(parkingsCount)
          : parkingsCount || 0
      const isPlural = count !== 1
      const isPolishFew = i18n.language === 'pl' && count >= 2 && count <= 4

      const translationKey = !isPlural
        ? 'parkings:hero.parkingSearchResultsAirportSingle'
        : isPolishFew
        ? 'parkings:hero.parkingSearchResultsAirportFew'
        : 'parkings:hero.parkingSearchResultsAirport'

      const airportText = t(
        translationKey as 'parkings:hero.parkingSearchResultsAirport',
        {
          value: parkingsCount,
          airportName: airportDisplayName
        }
      )
      return airportText
    } else if (!isAirport && cityName) {
      let cityDisplayName = cityName

      if (locale === 'pl' && locativeForm) {
        cityDisplayName = locativeForm
      } else if (locale === 'de') {
        const germanLocationContext = getLocationContext(cityName, 'pl')
        if (germanLocationContext.cityKey) {
          const germanName =
            GERMAN_CITY_NAMES[germanLocationContext.cityKey]?.[0]
          if (germanName) {
            cityDisplayName =
              germanName.charAt(0).toUpperCase() + germanName.slice(1)
          }
        }
      } else if (cityUrlSlug || cityKey) {
        const translationKey = cityUrlSlug || cityKey
        try {
          const translatedName = t(
            `common:menu.towns.${translationKey}` as any,
            {
              defaultValue: ''
            }
          )
          if (translatedName) {
            cityDisplayName = translatedName
          }
        } catch {
          cityDisplayName = cityName
        }
      }

      const count =
        typeof parkingsCount === 'string'
          ? parseInt(parkingsCount)
          : parkingsCount || 0
      const isPlural = count !== 1
      const isPolishFew = i18n.language === 'pl' && count >= 2 && count <= 4

      const translationKey = !isPlural
        ? 'parkings:hero.parkingSearchResultsCitySingle'
        : isPolishFew
        ? 'parkings:hero.parkingSearchResultsFew'
        : 'parkings:hero.parkingSearchResultsCity'

      const cityText = t(
        translationKey as 'parkings:hero.parkingSearchResultsCity',
        {
          value: parkingsCount,
          cityName: cityDisplayName
        }
      )
      return cityText
    }

    const count =
      typeof parkingsCount === 'string'
        ? parseInt(parkingsCount)
        : parkingsCount || 0
    const isPlural = count !== 1
    const isPolishFew = i18n.language === 'pl' && count >= 2 && count <= 4

    const fallbackKey = !isPlural
      ? 'parkings:hero.parkingSearchResultsSingle'
      : isPolishFew
      ? 'parkings:hero.parkingSearchResultsFew'
      : 'parkings:hero.parkingSearchResults'

    return (
      text ||
      t(fallbackKey as 'parkings:hero.parkingSearchResults', {
        value: parkingsCount
      })
    )
  }

  const headerText = getDynamicHeaderText()

  return (
    <HeroWrapper withoutContent={!isMobile} noPadding={isMobile}>
      {!isMobile && (
        <TitleWrapper style={{ textAlign: 'center' }}>
          <Title text={headerText} primary animated />
        </TitleWrapper>
      )}
    </HeroWrapper>
  )
}
